import { lockBodyScroll } from "../utils/scrollLock";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabase";
import { getBlockedUserIds } from "../utils/blocks";
import { getMutedUserIds } from "../utils/mutes";
import LazyImage from "./LazyImage";
import { IoClose, IoPricetag } from "react-icons/io5";
import { useFocusTrap } from "../utils/focusTrap";
import VerifiedBadge from "./VerifiedBadge";

const PAGE_SIZE = 20;

// Instagram-style tag popup, opened from the tag icon on any design card.
// Shows both directions of the "I made this!" relationship for `designId`:
// the original design it credits (if any) and every design that credits IT
// — plus a shortcut to credit it yourself. Everything is fetched on open,
// not eagerly per card, since most cards in a feed are never tagged at all.
export default function TagInfoModal({ designId, onClose }) {
  const navigate = useNavigate();

  const [inspiredSource, setInspiredSource] = useState(null);
  const [sourceLoading, setSourceLoading] = useState(true);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const bodyRef = useRef(null);
  const sentinelRef = useRef(null);
  const blockedIdsRef = useRef(new Set());
  const loadingMoreRef = useRef(false);
  const sheetRef = useRef(null);
  useFocusTrap(sheetRef, true);

  // Blocked/muted ids are fetched once up front and shared by both the
  // "Inspired by" source lookup and the remakes list below — a remake can
  // credit a design whose owner the viewer has since blocked or muted, and
  // without filtering that out too, the popup would still surface a link
  // into that person's content (the same class of bypass already fixed for
  // every other feed/list surface — Home, Comments, NotificationBell, etc.).
  useEffect(() => {
    let cancelled = false;
    setSourceLoading(true);
    setInspiredSource(null);
    setLoading(true);
    setHasMore(true);

    async function run() {
      const {
        data: { session }
      } = await supabase.auth.getSession();
      const me = session?.user ?? null;
      const [blockedIds, mutedIds] = await Promise.all([
        getBlockedUserIds(me?.id),
        getMutedUserIds(me?.id)
      ]);
      if (cancelled) return;
      blockedIdsRef.current = new Set([...blockedIds, ...mutedIds]);

      const [source, page] = await Promise.all([fetchInspiredSource(), fetchPage(0, PAGE_SIZE - 1)]);
      if (cancelled) return;

      if (source && !blockedIdsRef.current.has(source.users?.id)) {
        setInspiredSource(source);
      }
      setSourceLoading(false);

      const hasMoreNext = page.length === PAGE_SIZE;
      setRows(excludeBlocked(page));
      setOffset(PAGE_SIZE);
      setHasMore(hasMoreNext);
      setLoading(false);
    }

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designId]);

  async function fetchInspiredSource() {
    const { data } = await supabase
      .from("designs")
      .select("inspired_by_design_id")
      .eq("id", designId)
      .maybeSingle();
    if (!data?.inspired_by_design_id) return null;

    const { data: source } = await supabase
      .from("designs")
      .select("id, title, users(id, username)")
      .eq("id", data.inspired_by_design_id)
      .maybeSingle();
    return source || null;
  }

  useEffect(() => {
    const unlockScroll = lockBodyScroll();

    function onKeyDown(e) {
      if (e.key === "Escape") onClose?.();
    }
    window.addEventListener("keydown", onKeyDown);

    return () => {
      unlockScroll();
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  async function fetchPage(start, end) {
    const { data, error } = await supabase
      .from("designs")
      .select("id, title, image_url, images, users(id, username, avatar_url, verified)")
      .eq("inspired_by_design_id", designId)
      .order("created_at", { ascending: false })
      .range(start, end);

    if (error) {
      console.log("LOAD REMAKES ERROR:", error);
      return [];
    }
    return data || [];
  }

  function excludeBlocked(list) {
    const blocked = blockedIdsRef.current;
    if (blocked.size === 0) return list;
    return list.filter(d => !blocked.has(d.users?.id));
  }

  async function loadMore() {
    if (loadingMoreRef.current || !hasMore) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);

    const raw = await fetchPage(offset, offset + PAGE_SIZE - 1);
    const hasMoreNext = raw.length === PAGE_SIZE;
    setRows(prev => [...prev, ...excludeBlocked(raw)]);
    setOffset(prev => prev + PAGE_SIZE);
    setHasMore(hasMoreNext);
    loadingMoreRef.current = false;
    setLoadingMore(false);
  }

  useEffect(() => {
    const el = sentinelRef.current;
    const root = bodyRef.current;
    if (!el || !root || loading || !hasMore) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) loadMore();
      },
      { root, rootMargin: "200px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, hasMore, offset, loadingMore]);

  function goToDesign(id) {
    onClose?.();
    navigate(`/d/${id}`);
  }

  function goMakeThis() {
    onClose?.();
    navigate(`/upload?inspired_by=${designId}`);
  }

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) onClose?.();
  }

  return createPortal(
    <div className="taginfo-backdrop" onClick={handleBackdropClick}>
      <div className="taginfo-sheet" ref={sheetRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Tag info">
        <style>{`
          .taginfo-backdrop {
            position: fixed;
            inset: 0;
            background: rgba(20, 14, 8, 0.55);
            z-index: 1000;
            display: flex;
            align-items: flex-end;
            justify-content: center;
          }

          .taginfo-sheet {
            background: var(--bg-surface);
            width: 100%;
            max-height: 78vh;
            border-radius: 18px 18px 0 0;
            display: flex;
            flex-direction: column;
            box-shadow: 0 -4px 24px rgba(0,0,0,0.2);
            animation: taginfo-slide-up 0.22s ease-out;
          }

          @keyframes taginfo-slide-up {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }

          .taginfo-handle {
            width: 40px;
            height: 4px;
            border-radius: 2px;
            background: var(--border-input);
            margin: 10px auto 0;
            flex-shrink: 0;
          }

          .taginfo-header {
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            padding: 12px 16px;
            border-bottom: 1px solid var(--border-soft);
            flex-shrink: 0;
          }

          .taginfo-close {
            position: absolute;
            right: 12px;
            top: 50%;
            transform: translateY(-50%);
            display: flex;
            background: none;
            border: none;
            font-size: 22px;
            line-height: 1;
            cursor: pointer;
            color: var(--text-body);
            padding: 4px;
          }

          .taginfo-body {
            flex: 1;
            overflow-y: auto;
            padding: 4px 16px 16px;
            -webkit-overflow-scrolling: touch;
          }

          @media (min-width: 641px) {
            .taginfo-backdrop {
              align-items: center;
            }
            .taginfo-sheet {
              width: 92%;
              max-width: 420px;
              max-height: 78vh;
              border-radius: 16px;
              animation: taginfo-pop-in 0.18s ease-out;
            }
            .taginfo-handle {
              display: none;
            }
            @keyframes taginfo-pop-in {
              from { transform: scale(0.96); opacity: 0; }
              to { transform: scale(1); opacity: 1; }
            }
          }
        `}</style>

        <div className="taginfo-handle" />
        <div className="taginfo-header">
          <strong style={{ fontSize: 15 }}>🏷️ Tag info</strong>
          <button className="taginfo-close" onClick={onClose} aria-label="Close">
            <IoClose />
          </button>
        </div>

        <div className="taginfo-body" ref={bodyRef}>
          <button type="button" style={makeThisBtn} onClick={goMakeThis}>
            <IoPricetag size={16} /> I made this! Post your own version
          </button>

          {!sourceLoading && inspiredSource && (
            <div style={sectionLabel}>Inspired by</div>
          )}
          {!sourceLoading && inspiredSource && (
            <div style={row} onClick={() => goToDesign(inspiredSource.id)}>
              <div style={rowInfo}>
                <span style={rowUsername}>@{inspiredSource.users?.username || "unknown"}</span>
                {inspiredSource.title && <span style={rowTitle}>{inspiredSource.title}</span>}
              </div>
            </div>
          )}

          <div style={sectionLabel}>🧵 Made this</div>

          {loading ? (
            <p style={mutedText}>Loading…</p>
          ) : rows.length === 0 ? (
            <p style={mutedText}>No one has made this yet.</p>
          ) : (
            rows.map(d => (
              <div key={d.id} style={row} onClick={() => goToDesign(d.id)}>
                <LazyImage
                  src={d.images?.length > 0 ? d.images[0] : d.image_url}
                  alt={d.title}
                  style={rowThumb}
                />
                <div style={rowInfo}>
                  <span style={rowUsername}>
                    @{d.users?.username || "unknown"}
                    {d.users?.verified && <VerifiedBadge size={16} style={{ verticalAlign: -1 }} />}
                  </span>
                  {d.title && <span style={rowTitle}>{d.title}</span>}
                </div>
              </div>
            ))
          )}

          {!loading && hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}
          {loadingMore && <p style={mutedText}>Loading more…</p>}
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ===== STYLES (matches FollowListModal) ===== */

const makeThisBtn = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  width: "100%",
  padding: "10px 14px",
  borderRadius: 10,
  border: "none",
  background: "var(--accent-primary)",
  color: "var(--accent-contrast)",
  fontWeight: 700,
  fontSize: 13.5,
  cursor: "pointer",
  margin: "8px 0 4px"
};

const sectionLabel = {
  fontSize: 12,
  fontWeight: 700,
  color: "var(--text-muted)",
  textTransform: "uppercase",
  letterSpacing: 0.3,
  margin: "14px 0 4px"
};

const row = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "10px 0",
  borderBottom: "1px solid var(--border-soft)",
  cursor: "pointer"
};

const rowThumb = {
  width: 44,
  height: 44,
  borderRadius: 8,
  objectFit: "cover",
  border: "1px solid var(--border-medium)",
  flexShrink: 0
};

const rowInfo = {
  display: "flex",
  flexDirection: "column",
  minWidth: 0
};

const rowUsername = {
  fontWeight: 700,
  fontSize: 14,
  color: "var(--text-primary)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis"
};

const rowTitle = {
  fontSize: 12.5,
  color: "var(--text-muted)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis"
};

const mutedText = { fontSize: 13, color: "var(--text-muted)", margin: "8px 0" };
